import React, { useCallback, useEffect, useState } from 'react'
import { IoIosCheckmarkCircleOutline, IoIosRepeat, IoIosTrash, IoMdCheckmarkCircleOutline, IoMdClose, IoMdList, IoMdPricetags, IoMdSearch } from 'react-icons/io'
import Swal from 'sweetalert2'
import { ApprovePrice, DeletePrice, GetList } from '../Services/PriceServices';
import { PriceType, ResponseType } from '../Types/Prices';
import { useAuth } from '../utils/AuthContext';
import { formatDate } from '../utils/formatDate';
import { normalizeSearch } from '../utils/normalizeSearch';
import PriceDetailsModal from '../Components/PriceDetailsModal';

type FeedbackNotice = {
    type: 'success' | 'info' | 'warning' | 'error';
    title: string;
    message?: string;
    persistent?: boolean;
};

type BulkProgress = {
    completed: number;
    total: number;
    succeeded: number;
    failed: number;
};

function getPriceKey(price: PriceType): string {
    return `${price.Serial}-${price.Number}`;
}

function isUncertainFailure(result: ResponseType): boolean {
    return result.failureKind !== undefined && result.failureKind !== 'unauthorized';
}

function Home() {
    const [filteredPrices, setFilteredPrices] = useState<PriceType[]>([] as PriceType[]);
    const [searchText, setSearchText] = useState<string>('');
    const [generalPrices, setGeneralPrices] = useState<PriceType[]>([] as PriceType[]);
    const { user, companyId, apiToken: token, handleSessionExpired } = useAuth();
    const [repeatButtonVisible, setRepeatButtonVisible] = useState<boolean>(false);
    const [listStatus, setListStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [selectedPrice, setSelectedPrice] = useState<PriceType | null>(null);
    const [notification, setNotification] = useState<FeedbackNotice | null>(null);
    const [activeAction, setActiveAction] = useState<string | null>(null);
    const [bulkProgress, setBulkProgress] = useState<BulkProgress | null>(null);

    useEffect(() => {
        if (!notification || notification.persistent) return;
        const timeout = window.setTimeout(() => setNotification(null), 3200);
        return () => window.clearTimeout(timeout);
    }, [notification]);

    useEffect(() => {
        const query = normalizeSearch(searchText);
        if (query) {
            const filtered = generalPrices.filter(price => (
                normalizeSearch(price.Serial).includes(query)
                || normalizeSearch(price.Number.toString()).includes(query)
                || normalizeSearch(price.CreateUser).includes(query)
            ));
            setFilteredPrices(filtered);
        } else {
            setFilteredPrices(generalPrices);
        }
    }, [searchText, generalPrices]);

    const showSessionExpired = useCallback(async () => {
        await Swal.fire({
            icon: 'error',
            title: 'Oturum sona erdi',
            text: 'Devam etmek için tekrar giriş yapmanız gerekiyor.',
            confirmButtonText: 'Girişe dön',
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
                popup: 'border border-slate-700 bg-slate-900 text-white rounded-2xl',
                confirmButton: 'bg-cyan-400 text-slate-950 text-xs font-semibold rounded-lg',
            },
        });
        await handleSessionExpired();
    }, [handleSessionExpired]);

    const handleListPrice = useCallback(async (): Promise<boolean> => {
        if (!user || !token) return false;

        if (!companyId) {
            setListStatus('error');
            setNotification({ type: 'error', title: 'Firma seçilmedi', message: 'Listeyi yüklemek için firma seçmeniz gerekiyor.', persistent: true });
            return false;
        }

        setListStatus('loading');
        try {
            const response: ResponseType = await GetList(companyId, token);
            if (response.error === false) {
                setGeneralPrices(response.data ?? []);
                setRepeatButtonVisible(true);
                setListStatus('success');
                return true;
            } else {
                setListStatus('error');
                if (response.message === 'Oturum süreniz sona ermiş! Lütfen tekrar giriş yapın.') {
                    await showSessionExpired();
                } else {
                    setNotification({ type: 'error', title: 'Evrak listesi alınamadı', message: response.message, persistent: true });
                }
                return false;
            }
        } catch {
            setListStatus('error');
            setNotification({
                type: 'error',
                title: 'Bağlantı sorunu',
                message: 'Evrak listesi alınırken bir sorun oluştu. Bağlantınızı kontrol edip yeniden deneyin.',
                persistent: true,
            });
            return false;
        }
    }, [user, companyId, token, showSessionExpired]);

    useEffect(() => {
        handleListPrice();
    }, [user, handleListPrice]);

    const handleApprovePrice = async (price: PriceType) => {
        if (!user || !companyId || !token) return;

        const resultConfirm = await Swal.fire({
            icon: 'warning',
            iconColor: 'yellow',
            title: 'Evrak onaylansın mı?',
            text: `${price.Serial}-${price.Number} numaralı evrakta ${price.ProductCount} ürün ve ${price.StoreCount} şube için fiyat değişikliği onaylanacak. Bu işlem geri alınamaz. Onaylıyor musunuz?`,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: 'Evet, Onayla',
            cancelButtonText: 'Hayır',
            customClass: {
                popup: 'bg-slate-900 text-white rounded-2xl',
                title: 'text-base font-semibold',
                htmlContainer: 'text-sm leading-6 text-slate-300',
                confirmButton: 'bg-cyan-400 hover:bg-cyan-300 text-slate-950 text-sm font-semibold rounded-lg',
                cancelButton: 'bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg',
                icon: 'text-xs',
            },
        });

        if (!resultConfirm.isConfirmed) {
            setNotification({ type: 'info', title: 'Onaylama iptal edildi' });
            return;
        }

        const actionKey = `approve-${getPriceKey(price)}`;
        setNotification(null);
        setActiveAction(actionKey);
        let result: ResponseType;
        try {
            result = await ApprovePrice(companyId, price, token);
        } catch {
            result = {
                error: true,
                message: 'Onay sonucu sunucudan doğrulanamadı. Tekrar göndermeden önce listeyi yenileyip evrakın durumunu kontrol edin.',
                failureKind: 'network',
            };
        } finally {
            setActiveAction(null);
        }

        if (result.error === false) {
            const approvedPriceKey = getPriceKey(price);
            setGeneralPrices(currentPrices => currentPrices.filter(p => getPriceKey(p) !== approvedPriceKey));
            setSelectedPrice(null);
            setNotification({ type: 'success', title: 'Evrak onaylandı', message: `${price.Serial}-${price.Number} evrakı onaylandı.` });
        } else if (result.message === 'Oturum süreniz sona ermiş! Lütfen tekrar giriş yapın.') {
            await showSessionExpired();
        } else if (isUncertainFailure(result)) {
            setSelectedPrice(null);
            const refreshed = await handleListPrice();
            setNotification({ type: 'error', title: 'Onay sonucu doğrulanamadı', message: `${result.message} ${refreshed ? 'Liste yenilendi; evrakın güncel durumunu kontrol edin.' : 'Liste yenilenemedi; “Listeyi Yenile” ile tekrar kontrol edin.'} Otomatik tekrar gönderim yapılmadı.`, persistent: true });
        } else {
            setNotification({ type: 'error', title: 'Onaylama başarısız', message: result.message, persistent: true });
        }
    }

    const handleDeletePrice = async (price: PriceType) => {
        if (!user || !companyId || !token) return;

        const confirmation = await Swal.fire({
            icon: 'warning',
            iconColor: 'red',
            title: 'Evrak silinsin mi?',
            html: `
                <p class="text-sm leading-6 text-slate-300">${price.Serial}-${price.Number} numaralı evrak silinecek. Evrakta ${price.ProductCount} ürün ve ${price.StoreCount} şube için fiyat bilgisi bulunuyor. Bu işlem geri alınamaz.</p>
                <div class="mt-4 flex flex-row items-center justify-center gap-2">
                    <input type="checkbox" id="confirm-delete" name="confirm-delete" class="h-4 w-4 rounded border-slate-600 accent-rose-500" />
                    <label for="confirm-delete" class="text-sm text-slate-200">Silme işlemini onaylıyorum.</label>
                </div>
            `,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: 'Evet, Sil',
            cancelButtonText: 'Vazgeç',
            preConfirm: () => {
                const checkbox = Swal.getPopup()?.querySelector<HTMLInputElement>('#confirm-delete');
                if (!checkbox?.checked) {
                    Swal.showValidationMessage('Silmek için önce onay kutusunu işaretleyin.');
                    return false;
                }
                return true;
            },
            customClass: {
                popup: 'border border-slate-700 bg-slate-900 text-white rounded-2xl',
                title: 'text-base font-semibold',
                htmlContainer: 'text-sm leading-6 text-slate-300',
                confirmButton: 'bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold rounded-lg',
                cancelButton: 'bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg',
                icon: 'text-xs',
            },
        });

        if (!confirmation.isConfirmed) {
            setNotification({ type: 'info', title: 'Silme işlemi iptal edildi' });
            return;
        }

        setNotification(null);
        setActiveAction(`delete-${getPriceKey(price)}`);
        let result: ResponseType;
        try {
            result = await DeletePrice(companyId, price, token);
        } catch {
            result = {
                error: true,
                message: 'Silme sonucu sunucudan doğrulanamadı. Tekrar göndermeden önce listeyi yenileyip evrakın durumunu kontrol edin.',
                failureKind: 'network',
            };
        } finally {
            setActiveAction(null);
        }

        if (result.error === false) {
            const deletedPriceKey = getPriceKey(price);
            setGeneralPrices(currentPrices => currentPrices.filter(p => getPriceKey(p) !== deletedPriceKey));
            setNotification({ type: 'success', title: 'Evrak silindi', message: `${price.Serial}-${price.Number} evrakı silindi.` });
        } else if (result.message === 'Oturum süreniz sona ermiş! Lütfen tekrar giriş yapın.') {
            await showSessionExpired();
        } else if (isUncertainFailure(result)) {
            const refreshed = await handleListPrice();
            setNotification({ type: 'error', title: 'Silme sonucu doğrulanamadı', message: `${result.message} ${refreshed ? 'Liste yenilendi; evrakın güncel durumunu kontrol edin.' : 'Liste yenilenemedi; “Listeyi Yenile” ile tekrar kontrol edin.'} Otomatik tekrar gönderim yapılmadı.`, persistent: true });
        } else {
            setNotification({ type: 'error', title: 'Silme başarısız', message: result.message, persistent: true });
        }
    }

    const handleApproveAll = async (prices: PriceType[]) => {
        if (!user || !companyId || !token) return;

        const confirm = await Swal.fire({
            icon: 'warning',
            iconColor: '#facc15',
            html: `<h2 class="mb-4 text-lg font-bold">${prices.length} evrak onaylansın mı?</h2><p class="text-sm">Seçili listedeki evraklar onaylanacak. Bu işlem geri alınamaz.</p>`,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: 'Evet, Onayla',
            cancelButtonText: 'İptal',
            customClass: {
                popup: 'border border-slate-700 bg-slate-900 text-white rounded-2xl',
                htmlContainer: 'text-white',
                confirmButton: 'bg-cyan-400 hover:bg-cyan-300 text-slate-950 text-xs font-semibold rounded-lg',
                cancelButton: 'bg-slate-700 hover:bg-slate-600 text-white text-xs rounded-lg',
            },
        });

        if (!confirm.isConfirmed) {
            setNotification({ type: 'info', title: 'Toplu onay iptal edildi' });
            return;
        }

        setNotification(null);
        setActiveAction('bulk-approve');
        setBulkProgress({ completed: 0, total: prices.length, succeeded: 0, failed: 0 });
        let successResult = 0;
        let errorResults = 0;
        let technicalFailure: ResponseType | null = null;

        for (let index = 0; index < prices.length; index++) {
            const price = prices[index];
            let result: ResponseType;
            try {
                result = await ApprovePrice(companyId, price, token);
            } catch {
                result = {
                    error: true,
                    message: 'Onay sonucu sunucudan doğrulanamadı. İşlem durduruldu.',
                    failureKind: 'network',
                };
            }

            if (result.error === false) successResult++;
            else errorResults++;

            setBulkProgress({
                completed: index + 1,
                total: prices.length,
                succeeded: successResult,
                failed: errorResults,
            });

            if (result.message === 'Oturum süreniz sona ermiş! Lütfen tekrar giriş yapın.') {
                setBulkProgress(null);
                setActiveAction(null);
                await showSessionExpired();
                return;
            }

            if (isUncertainFailure(result)) {
                technicalFailure = result;
                break;
            }
        }

        setBulkProgress(null);
        setActiveAction(null);
        if (technicalFailure) {
            const remaining = prices.length - successResult - errorResults;
            const refreshed = await handleListPrice();
            setNotification({
                type: 'warning',
                title: 'Toplu onay durduruldu',
                message: `${successResult}/${prices.length} evrak kesin olarak onaylandı. ${errorResults} işlem başarısız veya sonucu belirsiz; ${remaining} evrak için istek gönderilmedi. ${refreshed ? 'Liste yenilendi; kalan evrakların durumunu kontrol edin.' : 'Liste yenilenemedi; “Listeyi Yenile” ile tekrar kontrol edin.'} ${technicalFailure.message} Otomatik tekrar gönderim yapılmadı.`,
                persistent: true,
            });
        } else if (errorResults === 0) {
            setNotification({ type: 'success', title: 'Toplu onay tamamlandı', message: `${successResult}/${prices.length} evrak onaylandı.` });
            await handleListPrice();
        } else if (successResult > 0) {
            setNotification({
                type: 'warning',
                title: 'Toplu onay kısmen tamamlandı',
                message: `${successResult}/${prices.length} evrak onaylandı, ${errorResults} evrak onaylanamadı. Hatalı evraklar listede kalıyor.`,
                persistent: true,
            });
            await handleListPrice();
        } else {
            setNotification({
                type: 'error',
                title: 'Toplu onay başarısız',
                message: `0/${prices.length} evrak onaylandı. Tekrar deneyebilir veya destek ekibine başvurabilirsiniz.`,
                persistent: true,
            });
        }
    }

    const handleSeeDetails = (price: PriceType) => {
        setSelectedPrice(price);
    }

    function formatTime(value: number) {
        if (typeof value !== 'number') {
            return null;
        }

        const hours = Math.floor(value);
        const minutes = (value - hours) * 60;

        const formattedHours = hours.toString().padStart(2, '0');
        const formattedMinutes = Math.round(minutes).toString().padStart(2, '0');

        return `${formattedHours}:${formattedMinutes}`;
    }

    const priceGridLayoutClass = filteredPrices.length === 1
        ? 'mx-auto max-w-lg grid-cols-1'
        : filteredPrices.length === 2
            ? 'mx-auto max-w-5xl grid-cols-1 md:grid-cols-2'
            : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4';

    return (
        <div className='w-full min-h-0 flex-1 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 pb-8'>
            {notification && (
                <div className="fixed right-4 top-4 z-[1300] w-[calc(100%-2rem)] max-w-md">
                    <div
                        role={notification.type === 'error' || notification.type === 'warning' ? 'alert' : 'status'}
                        aria-live={notification.type === 'error' ? 'assertive' : 'polite'}
                        className={`rounded-xl border border-l-4 bg-slate-900 p-4 text-slate-100 shadow-2xl shadow-black/40 ${
                            notification.type === 'success' ? 'border-emerald-700 border-l-emerald-400'
                                : notification.type === 'info' ? 'border-cyan-800 border-l-cyan-400'
                                    : notification.type === 'warning' ? 'border-amber-800 border-l-amber-400'
                                        : 'border-rose-800 border-l-rose-400'
                        }`}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-sm font-semibold">{notification.title}</p>
                                {notification.message && <p className="mt-1 text-xs leading-5 text-slate-300">{notification.message}</p>}
                                {!notification.persistent && <div className="mt-3 h-0.5 overflow-hidden rounded-full bg-slate-700"><div className="h-full w-full origin-left bg-cyan-400" style={{ animation: 'notification-progress 3.2s linear forwards' }} /></div>}
                            </div>
                            <button type="button" onClick={() => setNotification(null)} aria-label="Bildirimi kapat" className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300">
                                <IoMdClose aria-hidden="true" size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {bulkProgress && (
                <div className="fixed inset-x-0 top-0 z-[1100] border-b border-cyan-800 bg-slate-900/95 px-4 py-2 text-slate-100 shadow-lg backdrop-blur sm:px-6">
                    <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs">
                        <span role="status" aria-live="polite" className="font-medium">Toplu onay: {bulkProgress.completed}/{bulkProgress.total} evrak işlendi · {bulkProgress.succeeded} başarılı · {bulkProgress.failed} başarısız</span>
                        <span className="text-slate-400">İşlem tamamlanana kadar bekleyin.</span>
                    </div>
                    <div className="mx-auto mt-1.5 h-1 max-w-6xl overflow-hidden rounded-full bg-slate-700" role="progressbar" aria-label="Toplu onay ilerlemesi" aria-valuemin={0} aria-valuemax={bulkProgress.total} aria-valuenow={bulkProgress.completed}>
                        <div className="h-full bg-cyan-400 transition-[width] duration-300" style={{ width: `${(bulkProgress.completed / bulkProgress.total) * 100}%` }} />
                    </div>
                </div>
            )}
            {activeAction && !bulkProgress && (
                <div role="status" aria-live="polite" className="fixed inset-x-0 top-0 z-[1100] flex items-center justify-center gap-2 border-b border-cyan-800 bg-slate-900/95 px-4 py-2 text-xs font-medium text-slate-100 shadow-lg backdrop-blur">
                    <IoIosRepeat aria-hidden="true" className="animate-spin text-cyan-300" size={16} />
                    {activeAction.startsWith('approve-') ? 'Evrak onaylanıyor…' : 'Evrak siliniyor…'}
                </div>
            )}
            {selectedPrice && <PriceDetailsModal price={selectedPrice} onClose={() => setSelectedPrice(null)} onApprove={handleApprovePrice} isApproving={activeAction === `approve-${getPriceKey(selectedPrice)}`} />}
            <div className='container pt-2 mx-auto flex flex-col justify-center items-center'>
                <div className="w-full block flex-wrap justify-between items-center mx-auto text-white px-2">
                    <div className="w-full rounded-2xl border border-slate-800 bg-gradient-to-r from-slate-900 via-slate-900/90 to-transparent ps-4 pe-2 py-2 justify-between items-center mx-auto shadow-lg shadow-black/10">
                        <div className="flex w-full flex-col items-stretch justify-between gap-4 mx-auto md:flex-row md:items-center">
                            <div className="flex flex-col justify-center sm:mb-4 my-2">
                                <div className="flex w-full items-center">
                                    <IoMdPricetags className="me-2 text-cyan-300" size={'18px'} />
                                    <h4 className="text-sm font-semibold">Proforma Fiyat Değişikliği Onaylama</h4>
                                </div>
                                <p className="flex w-full text-xs text-slate-400" aria-live="polite">
                                    {listStatus === 'loading' && <span>Evraklar kontrol ediliyor…</span>}
                                    {listStatus === 'success' && generalPrices.length === 0 && <span className="text-slate-300">Onay bekleyen evrak bulunmuyor.</span>}
                                    {listStatus === 'success' && generalPrices.length > 0 && (
                                        <span>{generalPrices.length} evrak onay bekliyor.</span>
                                    )}
                                    {listStatus === 'error' && <span className="text-rose-300">Evrak listesi alınamadı.</span>}
                                </p>
                            </div>
                            <div className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:flex-wrap md:justify-center">
                                {filteredPrices.length > 0 &&
                                    <button disabled={Boolean(activeAction) || listStatus === 'loading'} onClick={() => handleApproveAll(filteredPrices)} type='button' className="flex h-9 w-full items-center justify-center rounded-lg bg-cyan-400 px-4 text-xs font-semibold text-slate-950 transition-colors hover:bg-cyan-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-300/30 disabled:cursor-wait disabled:opacity-60 sm:w-auto">
                                        {bulkProgress ? <IoIosRepeat className="mr-1 animate-spin" size={'16px'} /> : <IoIosCheckmarkCircleOutline className="mr-1" size={'16px'} />}
                                        {bulkProgress
                                            ? `Onaylanıyor ${bulkProgress.completed}/${bulkProgress.total}`
                                            : filteredPrices.length === 1 ? 'Evrak Fiyatlarını Onayla' : 'Listelenen Evrakları Onayla'}
                                    </button>
                                }
                                {(repeatButtonVisible || listStatus === 'error') &&
                                    <button disabled={Boolean(activeAction) || listStatus === 'loading'} onClick={() => handleListPrice()} type='button' className="flex h-9 w-full items-center justify-center rounded-lg border border-slate-700 bg-slate-800 px-4 text-xs text-slate-100 transition-colors hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-300/20 disabled:cursor-wait disabled:opacity-60 sm:w-auto">
                                        <IoIosRepeat className={`mr-1 ${listStatus === 'loading' ? 'animate-spin' : ''}`} size={'16px'} />
                                        {listStatus === 'loading' ? 'Yükleniyor…' : 'Listeyi Yenile'}
                                    </button>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                {generalPrices.length > 0 &&
                    <div className="mt-4 w-full px-2">
                        <label htmlFor="search" className="relative block w-full">
                            <span className="sr-only">Evraklarda ara</span>
                            <IoMdSearch aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                            <input
                                type="search"
                                id="search"
                                name="search"
                                placeholder="Seri, sıra no veya satın almacı adıyla ara"
                                className="h-11 w-full rounded-xl border border-slate-700 bg-slate-950/80 pl-10 pr-4 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400/15"
                                onChange={(event) => setSearchText(event.target.value)}
                                value={searchText}
                            />
                        </label>
                    </div>
                }
                {listStatus === 'success' && generalPrices.length === 0 && (
                    <div className="mx-2 mt-8 flex min-h-48 w-[calc(100%-1rem)] max-w-xl flex-col items-center justify-center rounded-2xl border border-dashed border-slate-700 bg-slate-900/60 px-6 py-8 text-center">
                        <IoMdPricetags aria-hidden="true" size={28} className="mb-3 text-cyan-300" />
                        <h3 className="text-base font-semibold text-slate-100">Onay bekleyen evrak yok</h3>
                        <p className="mt-2 text-sm leading-6 text-slate-400">Yeni bir proforma evrakı onaya geldiğinde burada görüntülenecek.</p>
                    </div>
                )}
                {listStatus === 'success' && generalPrices.length > 0 && filteredPrices.length === 0 && (
                    <div className="mx-2 mt-8 flex min-h-40 w-[calc(100%-1rem)] max-w-xl flex-col items-center justify-center rounded-2xl border border-dashed border-slate-700 bg-slate-900/60 px-6 py-8 text-center">
                        <h3 className="text-base font-semibold text-slate-100">Eşleşen evrak bulunamadı</h3>
                        <p className="mt-2 text-sm leading-6 text-slate-400">Arama ölçütünü değiştirin veya aramayı temizleyin.</p>
                        <button type="button" onClick={() => setSearchText('')} className="mt-4 rounded-lg bg-slate-800 px-4 py-2 text-xs text-slate-100 transition-colors hover:bg-slate-700">Aramayı Temizle</button>
                    </div>
                )}
                <div className={`mx-2 mt-4 grid w-[calc(100%-1rem)] gap-3 justify-center items-stretch ${priceGridLayoutClass}`}>
                    {filteredPrices && filteredPrices.map((price) => (
                        <div key={getPriceKey(price)} className='w-full rounded-2xl border border-slate-700 bg-slate-900/90 p-4 py-4 flex flex-col justify-center text-sm text-slate-100 shadow-lg shadow-black/10 ring-1 ring-white/5 transition-colors hover:border-cyan-700/80 hover:bg-slate-900'>
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-xs text-slate-400">Oluşturan</p>
                                    <p className="truncate font-medium text-slate-100" title={price.CreateUser}>{price.CreateUser}</p>
                                </div>
                                <div className="shrink-0 rounded-lg border border-slate-700 bg-slate-800 px-2.5 py-1 text-right">
                                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Evrak</p>
                                    <p className="font-semibold text-cyan-300">{price.Serial}-{price.Number}</p>
                                </div>
                            </div>
                            <div className="my-3 grid grid-cols-2 gap-3 border-y border-slate-800 py-3">
                                <div>
                                    <p className="text-xs text-slate-400">Oluşturulma</p>
                                    <p className="mt-1 font-medium text-slate-200">{formatDate(price.CreatedDate)}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-slate-400">Fiyat değişim tarihi</p>
                                    <p className="mt-1 font-medium text-slate-200">{formatDate(price.ChangedDate)}</p>
                                    <p className="text-xs text-slate-400">{formatTime(price.ChangedHour)}</p>
                                </div>
                            </div>
                            <div className="mb-4 grid grid-cols-3 divide-x divide-slate-800 rounded-xl border border-slate-800 bg-slate-950/40 py-2.5">
                                <div className="px-1 text-center">
                                    <p className="text-xs text-slate-400">Ürün</p>
                                    <p className="mt-1 text-lg font-semibold leading-5 text-slate-100">{price.ProductCount}</p>
                                </div>
                                <div className="px-1 text-center">
                                    <p className="text-xs text-slate-400">Şube</p>
                                    <p className="mt-1 text-lg font-semibold leading-5 text-slate-100">{price.StoreCount}</p>
                                </div>
                                <div className="px-1 text-center">
                                    <p className="text-xs text-slate-400">Satır</p>
                                    <p className="mt-1 text-lg font-semibold leading-5 text-cyan-300">{price.ProductList.length}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3 justify-between items-center">
                                <button className='w-full col-span-3 h-9 flex flex-row justify-center items-center px-2 border border-slate-700 bg-slate-800 rounded-lg focus:outline-none text-slate-100 hover:bg-slate-700 hover:border-slate-600 text-sm transition-colors' onClick={() => handleSeeDetails(price)} >
                                    <IoMdList className='me-1' size={'16px'} />
                                    Detayları Görüntüle
                                </button>
                                <button disabled={Boolean(activeAction) || listStatus === 'loading'} className='col-span-1 flex h-9 w-full flex-row items-center justify-center rounded-lg bg-rose-600 px-2 text-sm text-white transition-colors hover:bg-rose-500 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-rose-400/30 disabled:cursor-wait disabled:opacity-60' onClick={() => handleDeletePrice(price)} >
                                    {activeAction === `delete-${getPriceKey(price)}` ? <IoIosRepeat className='me-1 animate-spin' size={'16px'} /> : <IoIosTrash className='me-1' size={'16px'} />}
                                    {activeAction === `delete-${getPriceKey(price)}` ? 'Siliniyor…' : 'Sil'}
                                </button>
                                <button disabled={Boolean(activeAction) || listStatus === 'loading'} className='col-span-2 flex h-9 w-full flex-row items-center justify-center rounded-lg bg-cyan-400 px-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-cyan-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-300/30 disabled:cursor-wait disabled:opacity-60' onClick={() => handleApprovePrice(price)} >
                                    {activeAction === `approve-${getPriceKey(price)}` ? <IoIosRepeat className='me-1 animate-spin' size={'16px'} /> : <IoMdCheckmarkCircleOutline className='me-1' size={'16px'} />}
                                    {activeAction === `approve-${getPriceKey(price)}` ? 'Onaylanıyor…' : 'Onayla'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default Home
